import { useCallback, useEffect, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Input } from '@repo/videa-components/src/ui/input';
import { Redirect, useLocation } from 'react-router-dom';

import { endpoints } from '@/react-query/endpoints';

import { makeAPIRequestV2, setExternalJwtToken } from '../../../utils/api';

export const PatientDownloadableLinkHandler = () => {
  const location = useLocation();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accessCode, setAccessCode] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { t } = useLingui();

  const urlParams = new URLSearchParams(location.search);

  const shortCode = urlParams.get('shortCode');
  const shouldAskForAccessCode = urlParams.get('needsAccessCode') === 'true';

  const handleSubmit = useCallback(async () => {
    if (!shortCode) {
      setError(t`Invalid public link. Please check your URL and try again.`);
      return;
    }

    if (shouldAskForAccessCode && !accessCode.trim()) {
      setError(t`Please enter an access code.`);
      return;
    }

    setHasSubmitted(true);
    setIsLoading(true);
    setError(null);

    try {
      const response = await makeAPIRequestV2(
        `/${endpoints.v3}/${endpoints.parseShortCode}`,
        {
          data: {
            shortCode: shortCode,
            accessCode: accessCode.trim() || null,
          },
        },
        'POST',
      );

      // Set the external JWT token
      setExternalJwtToken(response.data.token);

      // Set the redirect URL to trigger the redirect
      setRedirectUrl(response.data.redirectUrl);
    } catch (err: any) {
      console.error('Error fetching redirect URL:', err);
      if (err?.response?.data?.statusCode === 403) {
        setError(t`Invalid access code`);
      } else if (err?.response?.data?.statusCode === 429) {
        setError(t`Too many attempts. Please request a new QR code.`);
      } else {
        setError(t`Failed to process your request. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [shortCode, shouldAskForAccessCode, accessCode, t]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // Only auto-redirect if no access code is needed
  useEffect(() => {
    if (!shortCode) {
      setError(t`Invalid public link. Please check your URL and try again.`);
      return;
    }

    if (!shouldAskForAccessCode) {
      handleSubmit();
    }
  }, [shortCode, shouldAskForAccessCode, t, handleSubmit]);

  if (shouldAskForAccessCode && !hasSubmitted) {
    return (
      <div className="vai-flex vai-flex-col vai-justify-center vai-items-center vai-min-h-screen vai-p-8 vai-self-center vai-align-middle vai-gap-4">
        <span className="vai-text-xl vai-font-medium vai-text-gray-700 vai-text-center">
          <Trans>Please enter your access code to continue</Trans>
        </span>
        <Input
          className="!vai-w-[200px]"
          placeholder={t`Access code`}
          value={accessCode}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setAccessCode(e.target.value)
          }
          onKeyPress={handleKeyPress}
        />
        <Button
          onClick={handleSubmit}
          disabled={!accessCode.trim()}
          className="!vai-w-[200px]"
        >
          <Trans>Submit</Trans>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="vai-flex vai-justify-center vai-items-center vai-min-h-screen vai-p-8">
        <span className="vai-text-xl vai-font-medium vai-text-gray-700">
          <Trans>Processing your request...</Trans>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vai-flex vai-justify-center vai-items-center vai-min-h-screen vai-p-8">
        <span className="vai-text-xl vai-font-medium vai-text-gray-700">
          {error}
        </span>
      </div>
    );
  }

  if (redirectUrl) {
    return <Redirect to={redirectUrl} />;
  }

  return null;
};
